import { animate, createTimeline, stagger, svg, type Timeline } from "animejs";
import { type CSSProperties, type DependencyList, type RefObject,useEffect, useRef, useSyncExternalStore } from "react";
import { getI18n } from "react-i18next";

import { DISTANCE, DURATION, EASE, STAGGER, THRESHOLD } from "@/5-shared/constants/motion";

/** Anything anime.js gives back that we may need to tear down on unmount. */
export type Revertible = { revert: () => void };

const REDUCE_QUERY = "(prefers-reduced-motion: reduce)";

function motionQuery(): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  return window.matchMedia(REDUCE_QUERY);
}

/**
 * True when the user asked the OS to minimise motion. Imperative, for the
 * inside of an effect, where every scene branches on it. Components that need
 * the value *during render* must use `useReducedMotion` instead, so the answer
 * stays consistent between the prerendered markup and the hydrated tree.
 */
export function prefersReducedMotion(): boolean {
  return motionQuery()?.matches ?? false;
}

/**
 * `prefersReducedMotion` as a subscription: re-renders when the OS setting is
 * toggled, and reports "motion allowed" for the prerender and hydration passes
 * so server and client always agree on the first tree.
 */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const q = motionQuery();
      q?.addEventListener("change", onChange);
      return () => q?.removeEventListener("change", onChange);
    },
    () => motionQuery()?.matches ?? false,
    () => false,
  );
}

/**
 * The active i18next language, tracked through its own `languageChanged` event.
 *
 * Threaded into every scene's effect deps so a client-side locale switch re-arms
 * it. On the client i18next is a stable singleton whose language mutates in place
 * (`I18nProvider` calls `changeLanguage`, never a remount), and Vike swaps the
 * page without a key, so an effect bound with `deps=[]` would otherwise never
 * re-run. Worse, re-keyed copy (titles, labels) replaces `[data-reveal]` nodes
 * with fresh, still-`opacity:0` ones the already-fired observer never reveals,
 * leaving whole sections blank until a hard reload. This is the one signal that
 * fires reliably on the switch; re-running teardown + setup re-reveals against
 * the current DOM.
 */
function useSceneLanguage(): string {
  return useSyncExternalStore(
    (onChange) => {
      const i18n = getI18n();
      i18n?.on("languageChanged", onChange);
      return () => i18n?.off("languageChanged", onChange);
    },
    () => getI18n()?.language ?? "it",
    // Server snapshot reads the same per-render instance, so a prerendered EN
    // page hydrates without a spurious re-arm.
    () => getI18n()?.language ?? "it",
  );
}

/**
 * Position in a `[data-h]` hero stagger. The entrance is a CSS animation so the
 * headline paints without waiting for this bundle (see `styles/index.css`);
 * `--i` is the only thing the markup has to supply.
 */
export function step(i: number): CSSProperties {
  return { "--i": i } as CSSProperties;
}

/** What `handles()` hands a scene, see below. */
type Handles = ReturnType<typeof handles>;

/** The DOM lookups every imperative scene opens with, bound to its root. */
export function handles(root: ParentNode) {
  return {
    /** may be missing */
    one: <E extends Element>(sel: string) => root.querySelector<E>(sel),
    /** asserted present, for markup the same component renders */
    must: <E extends Element>(sel: string) => root.querySelector<E>(sel)!,
    all: <E extends Element>(sel: string) => Array.from(root.querySelectorAll<E>(sel)),
  };
}

/** One `Revertible` that tears down a list of them, the shape every scene ends on. */
export function revertAll(anims: (Revertible | null | undefined)[]): Revertible {
  return { revert: () => anims.forEach((a) => a?.revert()) };
}

/** Paint the finished frame, what every `prefersReducedMotion()` branch does. */
export function settle(targets: Iterable<HTMLElement | SVGElement | null>) {
  for (const el of targets) {
    if (!el) continue;
    el.style.opacity = "1";
    el.style.transform = "none";
  }
}

/**
 * A monoline mark's one entrance: it draws letter by letter as a hairline, then
 * fills. Shared by the landing "dec" mark and the DEC Capital logo.
 */
export function drawMonoline(paths: SVGPathElement[]): Timeline {
  return createTimeline({ defaults: { ease: EASE.inOut } })
    .add(svg.createDrawable(paths), { draw: ["0 0", "0 1"], duration: 900, delay: stagger(STAGGER.loose) }, 0)
    .add(paths, { fillOpacity: [0, 1], duration: 400, ease: EASE.out }, 700);
}

/**
 * Start `play` the first time `el` scrolls into view, then stop observing. Drops into an
 * existing `useAnime` setup, the caller keeps its own measuring / reduced-motion branch
 * and just pushes the returned handle onto its revert list.
 */
export function onceInView(el: Element, play: () => Revertible | void, rootMargin: string = THRESHOLD.reveal): Revertible {
  let started: Revertible | undefined;
  const io = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        started = play() as Revertible | undefined;
        obs.disconnect();
      }
    },
    { rootMargin },
  );
  const cover = afterCover(() => io.observe(el));
  return {
    revert: () => {
      cover.revert();
      io.disconnect();
      started?.revert();
    },
  };
}

/**
 * The page-transition cover: something (the landing doors' expand) fills the screen
 * across a client navigation. While it does, `html[data-cover]` is set, entrances
 * wait (`afterCover` below, CSS `[data-cover] [data-h]` in styles/index.css), and
 * they play in view once it is gone, as on a reload instead of finishing under it.
 */
const COVER = "data-cover";

/** Raises the cover; call the returned `release` once it no longer hides the page. */
export function coverPage(): () => void {
  document.documentElement.setAttribute(COVER, "");
  return () => document.documentElement.removeAttribute(COVER);
}

/** Runs `run` once no cover hides the page (at once when there is none). */
function afterCover(run: () => void): Revertible {
  const html = document.documentElement;
  if (!html.hasAttribute(COVER)) {
    run();
    return { revert: () => {} };
  }
  const mo = new MutationObserver(() => {
    if (html.hasAttribute(COVER)) return;
    mo.disconnect();
    run();
  });
  mo.observe(html, { attributes: true, attributeFilter: [COVER] });
  return { revert: () => mo.disconnect() };
}

/**
 * Run an imperative anime.js `setup` once the container element is mounted.
 * Whatever `setup` returns (a timeline / animation / draggable / custom
 * `{ revert }`) is reverted on unmount so React re-renders and Vite HMR don't
 * leak running animations.
 */
export function useAnime<T extends HTMLElement = HTMLDivElement>(setup: (root: T) => Revertible | undefined, deps: DependencyList = []): RefObject<T | null> {
  const ref = useRef<T>(null);
  // Re-arm on a client-side locale switch (see `useSceneLanguage`).
  const lang = useSceneLanguage();
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const instance = setup(root);
    return () => instance?.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, lang]);
  return ref;
}

/**
 * A looping scene, paused while it is off screen.
 *
 * `useLoopInView` with the three lines every caller wrapped it in folded in:
 * the `handles()` binding, the reduced-motion bail, and the `playAll` over
 * however many tweens the scene ended up with. Return the tweens as an array,
 * or nothing, when the markup the scene needs isn't there.
 */
export function useLoopScene<T extends HTMLElement = HTMLDivElement>(
  setup: (h: Handles, root: T) => (Playable | null | undefined)[] | void,
  deps: DependencyList = [],
): RefObject<T | null> {
  return useLoopInView<T>((root) => {
    if (prefersReducedMotion()) return;
    const anims = setup(handles(root), root)?.filter(Boolean) as Playable[] | undefined;
    return anims?.length ? playAll(...anims) : undefined;
  }, deps);
}

/**
 * Staggered fade/slide-up reveal for every `[data-reveal]` element inside the
 * container, played once when it scrolls into view. Replaces the old
 * `motion` `whileInView` pattern. Honours `prefers-reduced-motion`.
 */
export function useOnScrollReveal<T extends HTMLElement = HTMLDivElement>(
  { y = DISTANCE.reveal, delayStep = STAGGER.base }: { y?: number; delayStep?: number } = {},
  // Re-arm when the revealed content changes. Client routing swaps a page's
  // props without remounting (`getPageElement` renders `<Page/>` with no key),
  // so a section reused across routes, e.g. the solution SuccessStory as you
  // move slug→slug, keeps its first observer and the new, still-`opacity:0`
  // cards never reveal. Pass a stable signature of the content to re-observe.
  deps: DependencyList = [],
): RefObject<T | null> {
  return useAnime<T>((root) => {
    const targets = handles(root).all<HTMLElement>("[data-reveal]");
    if (targets.length === 0) return;

    if (prefersReducedMotion()) {
      settle(targets);
      return;
    }

    return onceInView(root, () =>
      animate(targets, {
        opacity: [0, 1],
        translateY: [y, 0],
        duration: DURATION.reveal,
        delay: stagger(delayStep),
        ease: EASE.out,
      }),
    );
  }, deps);
}

/**
 * The three frames a scroll-triggered scene has to describe.
 *
 * Every such scene on the site was writing the same eight lines by hand, branch
 * on reduced motion, paint the finished frame or set the start one, open an
 * `IntersectionObserver`, collect the handles, hand back a combined `revert`.
 * `useRevealScene` owns that skeleton; this is the part that differs.
 */
type RevealScene = {
  /** The finished frame, painted straight away when the user asked for less motion. */
  still: () => void;
  /**
   * The start frame. Runs immediately, not on intersection, setting it late
   * would show the finished state until the scroll reaches the section. Whatever
   * it builds that must be undone (a text split) comes back as a handle, reverted
   * with the scene even if it never played.
   */
  start?: () => Revertible | void;
  /** Built once the section scrolls in. Push every handle through `add`. */
  play: (add: (a: Revertible | null | undefined) => void) => void;
  /** Passed through to `onceInView`. */
  rootMargin?: string;
};

/**
 * A scene that plays once, the first time it scrolls into view.
 *
 * `setup` runs on mount with the root's `handles()` already bound; return the
 * three frames above, or nothing at all to opt out (the markup isn't there yet,
 * the measurement came back zero). Teardown is handled for you.
 */
export function useRevealScene<T extends HTMLElement = HTMLDivElement>(
  setup: (h: Handles, root: T) => RevealScene | void,
  deps: DependencyList = [],
): RefObject<T | null> {
  return useAnime<T>((root) => {
    const scene = setup(handles(root), root);
    if (!scene) return;

    if (prefersReducedMotion()) {
      scene.still();
      return;
    }

    const anims: (Revertible | null | undefined)[] = [scene.start?.() ?? undefined];
    // Handles are captured through `add`, so `play`'s own return value is not a
    // teardown handle; swallow it (a `(add) => add(...)` scene returns the
    // `anims.push` count, which `onceInView` must not later call `.revert()` on).
    anims.push(onceInView(root, () => void scene.play((a) => anims.push(a)), scene.rootMargin));
    return revertAll(anims);
  }, deps);
}

/** Anything with the play/pause/revert surface anime.js timelines expose. */
export type Playable = Revertible & { play: () => void; pause: () => void };

type CameraMove = {
  x: readonly [number, number];
  y: readonly [number, number];
  scale: readonly [number, number];
  ms: number;
};

/** Initial frame shared by every slow camera drift. */
export const cameraStartTransform = ({ x, y, scale }: CameraMove) => `translate(${x[0]}px, ${y[0]}px) scale(${scale[0]})`;

/** Loop a camera drift on the first image inside a stable, observed wrapper. */
export function useCameraDrift<T extends HTMLElement = HTMLDivElement>(camera: CameraMove): RefObject<T | null> {
  return useLoopInView<T>(
    (root) => {
      const img = root.querySelector<HTMLImageElement>("img");
      if (!img) return;
      const { x, y, scale, ms } = camera;
      const timeline = createTimeline({ autoplay: false, loop: true, defaults: { ease: EASE.inOutSoft } })
        .add(img, { translateX: [x[0], x[1]], translateY: [y[0], y[1]], scale: [scale[0], scale[1]], duration: ms })
        .add(img, { translateX: x[0], translateY: y[0], scale: scale[0], duration: ms });

      return {
        play: () => {
          img.style.willChange = "transform";
          timeline.play();
        },
        pause: () => {
          timeline.pause();
          img.style.willChange = "";
        },
        revert: () => {
          timeline.revert();
          img.style.willChange = "";
        },
      };
    },
    [camera],
  );
}

/**
 * The in-view margin every looping scene pauses against, anime timelines here,
 * raw canvases in `useCanvasLoop`. The -5% keeps a sliver at the bottom edge from
 * counting as "on screen".
 */
export const LOOP_ROOT_MARGIN = THRESHOLD.loop;

/**
 * One `Playable` driving a list of them together, the shape a scene with more
 * than one looping tween has to return to `useLoopInView`.
 */
function playAll(...anims: Playable[]): Playable {
  return {
    play: () => anims.forEach((a) => a.play()),
    pause: () => anims.forEach((a) => a.pause()),
    revert: () => anims.forEach((a) => a.revert()),
  };
}

/**
 * Run a looping anime.js `setup` and only let it play while the container is on
 * screen. Off-screen it pauses (no wasted CPU on unseen backdrops); anime.js
 * itself already pauses when the tab is hidden. Under `prefers-reduced-motion`
 * the timeline is built then immediately paused so a clean static frame shows.
 * Reverts on unmount, mirroring `useAnime`.
 */
export function useLoopInView<T extends HTMLElement = HTMLDivElement>(
  setup: (root: T) => Playable | undefined,
  deps: DependencyList = [],
): RefObject<T | null> {
  const ref = useRef<T>(null);
  // Re-arm on a client-side locale switch (see `useSceneLanguage`).
  const lang = useSceneLanguage();
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const instance = setup(root);
    if (!instance) return;

    if (prefersReducedMotion()) {
      instance.pause();
      return () => instance.revert();
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) instance.play();
          else instance.pause();
        }
      },
      { rootMargin: LOOP_ROOT_MARGIN },
    );
    io.observe(root);
    return () => {
      io.disconnect();
      instance.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, lang]);
  return ref;
}
